import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Sparkles, Trophy, ChevronLeft, ChevronRight, Star, Zap, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { API } from "@/App";

/**
 * Premium animated "Stars of the Day" section.
 *
 *  - Spotlight gradient background
 *  - Per-card: gold crown, glowing border, "STAR OF THE DAY" ribbon tape,
 *    trophy badge, animated sparkles
 *  - Below the carousel: a Boost Your Profile CTA that opens a modal
 *    listing the actionable steps required to be featured.
 *
 * Props:
 *   stars: array of star objects { user_id, name, photo_url, category, bio, ... }
 *   currentUser: optional — the logged-in user (used to hide the CTA when
 *                the user themselves is already a Star of the Day)
 */
export default function StarsOfTheDaySection({ stars = [], currentUser = null }) {
  const navigate = useNavigate();
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  if (!Array.isArray(stars) || stars.length === 0) return null;

  const goPrev = () => setActiveIdx((i) => (i - 1 + stars.length) % stars.length);
  const goNext = () => setActiveIdx((i) => (i + 1) % stars.length);

  const userIsStarOfDay = currentUser && stars.some((s) => s.user_id === currentUser?.user_id);

  return (
    <>
      {/* Inject keyframes for the custom animations (kept inline to avoid
          touching global CSS). */}
      <style>{`
        @keyframes sotd-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes sotd-spotlight {
          0%, 100% { opacity: 0.55; transform: translateX(-15%) rotate(-12deg); }
          50%      { opacity: 0.85; transform: translateX(15%)  rotate(-12deg); }
        }
        @keyframes sotd-twinkle {
          0%, 100% { opacity: 0.35; transform: scale(0.9); }
          50%      { opacity: 1;    transform: scale(1.15); }
        }
        @keyframes sotd-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @keyframes sotd-glow {
          0%, 100% { box-shadow: 0 0 24px 4px rgba(255, 200, 60, 0.45), 0 0 64px 16px rgba(255, 105, 180, 0.35); }
          50%      { box-shadow: 0 0 40px 10px rgba(255, 200, 60, 0.70), 0 0 80px 24px rgba(255, 105, 180, 0.55); }
        }
        .sotd-shimmer-text {
          background: linear-gradient(90deg, #FFE066 0%, #FFFFFF 25%, #FFE066 50%, #FFFFFF 75%, #FFE066 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: sotd-shimmer 3s linear infinite;
        }
      `}</style>

      <section className="relative overflow-hidden rounded-3xl my-4">
        {/* === Stage / Spotlight background === */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0b2e] via-[#3b1361] to-[#1a0b2e]" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, rgba(255,210,80,0.35) 0%, transparent 55%)",
          }}
        />
        {/* Two moving spotlights */}
        <div
          className="absolute -top-10 left-1/4 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              "conic-gradient(from 90deg at 50% 0%, rgba(255,210,80,0.5), transparent 50%)",
            filter: "blur(20px)",
            animation: "sotd-spotlight 6s ease-in-out infinite",
          }}
        />
        <div
          className="absolute -top-10 right-1/4 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              "conic-gradient(from 270deg at 50% 0%, rgba(255,105,180,0.4), transparent 50%)",
            filter: "blur(24px)",
            animation: "sotd-spotlight 8s ease-in-out infinite reverse",
          }}
        />

        {/* Floating sparkles */}
        {[...Array(6)].map((_, i) => (
          <Sparkles
            key={i}
            className="absolute text-yellow-300 pointer-events-none"
            style={{
              top: `${10 + (i * 13) % 70}%`,
              left: `${(i * 17) % 90}%`,
              width: 14 + (i % 3) * 4,
              height: 14 + (i % 3) * 4,
              animation: `sotd-twinkle ${2 + (i % 3)}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
              opacity: 0.6,
            }}
          />
        ))}

        {/* === Header === */}
        <div className="relative z-10 px-4 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-300" />
            <h2 className="font-outfit font-bold text-white text-base sm:text-lg tracking-wide">
              <span className="sotd-shimmer-text">★ STARS OF THE DAY ★</span>
            </h2>
          </div>
          {stars.length > 1 && (
            <div className="flex items-center gap-1 text-white/70 text-xs">
              <span className="font-mono">{activeIdx + 1}</span>
              <span>/</span>
              <span className="font-mono">{stars.length}</span>
            </div>
          )}
        </div>

        {/* === Card carousel === */}
        <div className="relative z-10 px-4 pb-4">
          <div className="relative">
            {/* Prev/Next buttons (only if multiple) */}
            {stars.length > 1 && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Stage with all cards — horizontal scroll, active scaled */}
            <div className="flex items-stretch justify-center gap-2 sm:gap-4 px-8 sm:px-10 overflow-hidden">
              {stars.map((s, idx) => {
                const isActive = idx === activeIdx;
                return (
                  <PremiumStarCard
                    key={s.user_id || idx}
                    star={s}
                    isActive={isActive}
                    onClick={() => {
                      if (isActive) navigate(`/star/${s.user_id}`);
                      else setActiveIdx(idx);
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* === Boost CTA === */}
          {!userIsStarOfDay && (
            <div className="mt-4 flex justify-center">
              <Button
                onClick={() => setShowBoostModal(true)}
                className="bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-500 hover:opacity-90 text-white font-bold rounded-full px-6 py-2 shadow-lg"
                data-testid="boost-profile-cta"
              >
                <Zap className="w-4 h-4 mr-1.5" />
                Want to be featured? Boost Your Profile
              </Button>
            </div>
          )}
          {userIsStarOfDay && (
            <p className="mt-4 text-center text-yellow-200 font-semibold text-sm">
              🎉 You are one of today's Stars! Keep shining ✨
            </p>
          )}
        </div>
      </section>

      {/* === Boost Your Profile modal === */}
      <BoostProfileModal
        open={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        onNavigate={(path) => {
          setShowBoostModal(false);
          navigate(path);
        }}
      />
    </>
  );
}

/* ----------------------- Premium Star Card ----------------------- */

function PremiumStarCard({ star, isActive, onClick }) {
  const photoUrl = star.photo_url
    ? star.photo_url.startsWith("http")
      ? star.photo_url
      : `${API.replace("/api", "")}${star.photo_url.startsWith("/api/") ? star.photo_url : "/api/files/" + star.photo_url}`
    : null;

  const category =
    star.primary_category ||
    (Array.isArray(star.categories) && star.categories[0]) ||
    star.category ||
    "Creator";
  const prettyCategory = String(category)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

  return (
    <button
      onClick={onClick}
      className={`relative shrink-0 transition-all duration-500 ${
        isActive ? "scale-100 z-10" : "scale-[0.7] sm:scale-75 opacity-60 hover:opacity-90"
      }`}
      style={{
        width: isActive ? "min(260px, 65vw)" : "min(160px, 35vw)",
        animation: isActive ? "sotd-float 4s ease-in-out infinite" : "none",
      }}
    >
      {/* Glowing border */}
      <div
        className="relative rounded-3xl overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #FFD700 0%, #FF69B4 50%, #FFD700 100%)",
          padding: 3,
          animation: isActive ? "sotd-glow 2.5s ease-in-out infinite" : "none",
        }}
      >
        <div className="relative bg-gradient-to-br from-[#2d1b4e] via-[#4a1e6f] to-[#2d1b4e] rounded-[22px] overflow-hidden">
          {/* Diagonal "STAR OF THE DAY" ribbon/tape */}
          <div
            className="absolute z-10 pointer-events-none"
            style={{
              top: 18,
              left: -34,
              transform: "rotate(-35deg)",
              width: 150,
            }}
          >
            <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-500 text-white text-[9px] font-extrabold tracking-widest text-center py-1 shadow-lg">
              STAR OF THE DAY
            </div>
          </div>

          {/* Crown */}
          <div
            className="absolute top-2 right-2 z-10"
            style={{
              animation: isActive ? "sotd-float 3s ease-in-out infinite" : "none",
            }}
          >
            <Crown className="w-6 h-6 text-yellow-300 drop-shadow-[0_0_8px_rgba(255,215,0,0.8)]" />
          </div>

          {/* Photo */}
          <div className="aspect-[4/5] w-full overflow-hidden bg-[#1a0b2e]">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={star.name}
                className="w-full h-full object-cover object-top"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Star className="w-16 h-16 text-yellow-300/40" />
              </div>
            )}
            {/* Dark gradient at bottom for legibility */}
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          </div>

          {/* Footer info */}
          <div className="absolute inset-x-0 bottom-0 p-3 z-10">
            <div className="flex items-center gap-1.5 mb-1">
              <Trophy className="w-3.5 h-3.5 text-yellow-300" />
              <span className="text-[10px] uppercase tracking-wider text-yellow-200 font-bold">
                Featured Today
              </span>
            </div>
            <h4 className="font-outfit font-bold text-white text-base leading-tight truncate">
              {star.name}
            </h4>
            <p className="text-[11px] text-white/80 truncate">{prettyCategory}</p>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ----------------------- Boost Profile modal ----------------------- */

function BoostProfileModal({ open, onClose, onNavigate }) {
  const steps = [
    {
      icon: "📸",
      title: "Add 5+ portfolio items",
      desc: "Photos & videos that showcase your best work",
      cta: "Edit Profile",
      action: () => onNavigate("/dashboard?tab=talent"),
    },
    {
      icon: "💎",
      title: "Become Pro Creator",
      desc: "Stand out with a blue tick — only ₹199 one-time",
      cta: "Get Pro",
      action: () => onNavigate("/influencer/home"),
    },
    {
      icon: "📱",
      title: "Follow @foundstar_ on Instagram",
      desc: "Tag us in your stories and we'll re-share",
      cta: "Follow Now",
      action: () => {
        window.open("https://www.instagram.com/foundstar_", "_blank", "noopener,noreferrer");
      },
    },
    {
      icon: "🎯",
      title: "Complete your first collab",
      desc: "Brands trust creators with completed bookings",
      cta: null,
      action: null,
    },
    {
      icon: "⭐",
      title: "Maintain a 4.5+ rating",
      desc: "Deliver great work and ask brands for reviews",
      cta: null,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        {/* Premium header */}
        <div className="relative p-5 bg-gradient-to-br from-[#1a0b2e] via-[#4a1e6f] to-[#1a0b2e] text-white">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-white/70 hover:text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-5 h-5 text-yellow-300" />
            <h3 className="font-bold text-lg">Become Tomorrow's Star</h3>
          </div>
          <p className="text-white/80 text-sm">
            Complete these steps to maximise your chance of being featured as a
            Star of the Day.
          </p>
        </div>

        {/* Steps */}
        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto bg-white">
          {steps.map((s, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-pink-50 to-yellow-50 border border-pink-100"
            >
              <span className="text-2xl">{s.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1E293B] text-sm">{s.title}</p>
                <p className="text-xs text-[#64748B]">{s.desc}</p>
              </div>
              {s.cta && (
                <Button
                  size="sm"
                  onClick={s.action}
                  className="bg-[#FF69B4] hover:bg-[#FF69B4]/90 text-white rounded-full text-[11px] h-7 px-3"
                >
                  {s.cta}
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
